import { SyntheticEvent, useState } from "react";

import { Popup } from "semantic-ui-react";

import { Moment } from "moment";

import CardScheduleTimeShift from "./CardScheduleTimeShift";

import {
  ApplyNewTimeParamsType,
  ApproveTimeParamsType,
  ClockRecordParamsType,
  ClockRecordType,
  EventsTimeShiftType,
  LEAVE_OPTION_MAPPING,
} from "./sequence/SetupWorkSchedule";

type CardTimeShiftCellProps = {
  clockRecords?: ClockRecordType[];
  dayInCalendar: Moment;
  isReadonly?: boolean;
  isSelectionMode?: boolean;
  isViewShiftCodeSummary?: boolean;
  staff: { id?: number; employee_code: string; events: EventsTimeShiftType[]; };
  onApplyNewTime?: (data: ApplyNewTimeParamsType) => void;
  onApproveTime?: (data: ApproveTimeParamsType) => void;
  onGetClockRecord?: (data: ClockRecordParamsType) => void;
  onSelectEvent?: (event: EventsTimeShiftType, e: SyntheticEvent) => void;
};

const IMAGES = {
  al: "/static/images/approved-icon.png",
  ao: "/static/images/approved-icon.png",
  aw: "/static/images/approved-icon.png",
  ax: "/static/images/absent-icon.png",
  ro: "/static/images/pending-approval-icon.png",
  rw: "/static/images/pending-approval-icon.png",
  rx: "/static/images/pending-approval-icon.png",
};

const POPUP_OFFSET: [number, number] = [-10, 0];

const CardTimeShiftCell = (props: CardTimeShiftCellProps) => {
  const [openPopupId, setOpenPopupId] = useState<string | null>(null);

  const dailyEvents = props.staff.events
    .map((event, index) => ({ ...event, index }))
    .filter((item) => item.start.isSame(props.dayInCalendar, "day"))
    .sort((a, b) => {
      // ให้ leave_option มาก่อนเสมอ
      if (a.leave_option && !b.leave_option) {
        return -1; // a อยู่ก่อน b
      }

      if (!a.leave_option && b.leave_option) {
        return 1; // a อยู่หลัง b
      }

      // ถ้า leave_option เหมือนกัน ให้เรียงตาม start ก่อน
      const startDiff = a.start.valueOf() - b.start.valueOf();

      if (startDiff !== 0) {
        return startDiff;
      }

      // ถ้า start เท่ากัน ให้เรียงตาม end
      return a.end.valueOf() - b.end.valueOf();
    });

  return (
    <div className={props.isViewShiftCodeSummary ? "view-summary-cell" : ""}>
      {dailyEvents.map((event, eventIndex) => {
        const shiftStatus = event.shift_status.toLowerCase();
        const approvalStatus = (event.approval_status || "").toLowerCase();
        const statusIcon = (IMAGES as Record<string, string>)[`${approvalStatus}${shiftStatus}`];

        const allWorkShift = dailyEvents.flatMap((item) => item.work_shift ?? []);
        const leaveTime = allWorkShift.find((item) => event.id === item.id);
        const key = `event-${eventIndex}-${props.dayInCalendar.format("YYYY-MM-DD")}`;
        const id = `${props.staff.employee_code}-${props.dayInCalendar.format("YYYY-MM-DD")}-${eventIndex}`;

        return (
          <Popup
            key={key}
            disabled={!props.isSelectionMode || !!event.leave_option}
            open={openPopupId === key}
            position="bottom center"
            on="click"
            onClose={() => {
              setOpenPopupId(null);
            }}
            onOpen={() => {
              setOpenPopupId(key);
            }}
            content={
              <CardScheduleTimeShift
                id={id}
                clockRecords={props.clockRecords}
                dailyEvents={dailyEvents}
                data={event}
                dayInCalendar={props.dayInCalendar}
                index={event.index}
                isReadonly={props.isReadonly}
                leaveTime={leaveTime}
                open={openPopupId === key}
                shiftLength={dailyEvents.filter((item) => !item.leave_option).length}
                onApplyNewTime={props.onApplyNewTime}
                onApproveTime={props.onApproveTime}
                onGetClockRecord={props.onGetClockRecord}
              />
            }
            trigger={
              <div
                id={`card-time-shift-${id}`}
                className={event.leave_option ? "leave-slot" : ""}
                style={{ background: event.shift_status === "O" ? "" : event.color_code }}
                onClick={(e) => {
                  e.stopPropagation();

                  props.onSelectEvent?.(event, e);
                }}
              >
                {props.isViewShiftCodeSummary ? (
                  <Popup
                    content={event.title}
                    disabled={!event.leave_option}
                    offset={POPUP_OFFSET}
                    position="top left"
                    trigger={
                      <div className={`leave-slot-${event.approval_status}`}>
                        {event.leave_option ? event.title[0] : event.shift_status === "O" && "OFF"}
                      </div>
                    }
                  />
                ) : (
                  <>
                    {event.shift_status !== "O" && !event.leave_option && (
                      <div
                        style={{
                          alignItems: "center",
                          display: "grid",
                          gridTemplateColumns: "1fr auto",
                        }}
                      >
                        <div
                          style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {event.title}
                        </div>
                        {!!statusIcon && (
                          <img style={{ width: "12px" }} alt="leave-status.icon" src={statusIcon} />
                        )}
                      </div>
                    )}
                    {event.shift_status === "O" && (
                      <div className="slot-off">
                        OFF
                        {!!statusIcon && (
                          <img style={{ width: "12px" }} alt="shift-status.icon" src={statusIcon} />
                        )}
                      </div>
                    )}
                    {event.shift_status !== "O" && !event.leave_option && (
                      <>
                        {event.start.format("HH:mm")} - {event.end.format("HH:mm")}
                      </>
                    )}
                    {!!event.leave_option && (
                      <>
                        <div>{event.title}</div>
                        <div>
                          {event.work_shift?.length ? (
                            <>
                              {event.work_shift.map((ws, index) => (
                                <div key={`work-shift-${index}`}>{`${ws.start.format(
                                  "HH:mm"
                                )} - ${ws.end.format("HH:mm")}`}</div>
                              ))}
                            </>
                          ) : (
                            LEAVE_OPTION_MAPPING[event.leave_option] ||
                            `${event.start.format("HH:mm")} - ${event.end.format("HH:mm")}`
                          )}
                        </div>
                        <div className={`leave-slot-${event.approval_status}`}>
                          {event.approval_status === "A" ? "APPROVE" : "PENDING"}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            }
          />
        );
      })}
    </div>
  );
};

export default CardTimeShiftCell;
